import { ExpectedError } from "../../expectedError";
import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "../mongodbAtlas/logo.svg";

const PASSWORD_PLACEHOLDER = "__TINES_MONGODB_PASSWORD__";
const DEFAULT_PORT = "27017";
const DEFAULT_AUTH_SOURCE = "admin";

function hostComponent(host: string | undefined) {
  const value = (host ?? "").trim();
  if (/^\[[0-9a-fA-F:.]+\]$/.test(value)) return value;
  if (!/^[A-Za-z0-9._-]+$/.test(value)) {
    throw new ExpectedError("Invalid MongoDB host");
  }
  return value;
}

function portComponent(port: string | undefined) {
  const value = (port || DEFAULT_PORT).trim();
  if (!/^\d+$/.test(value)) throw new ExpectedError("Invalid MongoDB port");
  const portNumber = Number(value);
  if (portNumber < 1 || portNumber > 65535) {
    throw new ExpectedError("Invalid MongoDB port");
  }
  return value;
}

function connectionString({
  host,
  port,
  scheme,
  username,
  authSource,
  tls,
}: Record<string, string>) {
  const mongoHost = hostComponent(host);
  const srv = scheme === "mongodb+srv";
  const mongoPort = srv ? "" : `:${portComponent(port)}`;
  const encodedUsername = encodeURIComponent(username ?? "");
  const database = authSource?.trim() || DEFAULT_AUTH_SOURCE;
  const params = new URLSearchParams({ authSource: database });
  if (tls !== "false") params.set("tls", "true");
  const uriScheme = srv ? "mongodb+srv" : "mongodb";
  return `${uriScheme}://${encodedUsername}:${PASSWORD_PLACEHOLDER}@${mongoHost}${mongoPort}/${encodeURIComponent(database)}?${params}`;
}

export const mongodb = defineConnector({
  identifier: "mongodb",
  name: "MongoDB",
  tagLine: "Document database",
  logo,
  tags: ["data"],
  protocol: "mongodb",
  connectionTest: { protocol: "mongodb", connectionString },
  defaultUrlPattern: () => "",
  customizeStepHandledInline: true,
  auth: customAuth("mongodb"),
  steps: [
    {
      title: "Enter your MongoDB connection",
      items: [
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "MongoDB",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "select",
          label: "Connection scheme",
          defaultValue: "mongodb",
          target: { kind: "configuration", field: "scheme" },
          options: [
            { value: "mongodb", label: "mongodb:// (host and port)" },
            {
              value: "mongodb+srv",
              label: "mongodb+srv:// (DNS seed list)",
            },
          ],
        },
        {
          kind: "input",
          label: "Host",
          required: true,
          placeholder: "db.example.com",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "Port",
          required: true,
          defaultValue: "27017",
          target: { kind: "configuration", field: "port" },
          visibleWhen: [
            {
              target: { kind: "configuration", field: "scheme" },
              notValue: "mongodb+srv",
            },
          ],
        },
        {
          kind: "input",
          label: "Username",
          required: true,
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "input",
          label: "Password",
          required: true,
          target: { kind: "secrets", field: "password" },
        },
        {
          kind: "input",
          label: "Auth database",
          required: true,
          defaultValue: "admin",
          target: { kind: "configuration", field: "authSource" },
        },
        {
          kind: "select",
          label: "TLS",
          required: true,
          defaultValue: "true",
          target: { kind: "configuration", field: "tls" },
          options: [
            { value: "true", label: "Enabled" },
            { value: "false", label: "Disabled" },
          ],
        },
        {
          kind: "file",
          label: "CA certificate",
          accept: ".pem,.crt,.cer",
          helperText:
            "Upload a PEM CA certificate for private or self-signed MongoDB servers.",
          target: { kind: "configuration", field: "tlsCaCertificate" },
          visibleWhen: [
            {
              target: { kind: "configuration", field: "tls" },
              value: "true",
            },
          ],
        },
      ],
    },
  ],
  publicEnvVars: (configuration) => ({
    MONGODB_URL: connectionString(configuration),
  }),
  usageHint: () =>
    "Use process.env.MONGODB_URL as the MongoDB connection string.",
});
